import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import {
  api,
  ApiError,
  bootToken,
  setToken,
  type BrandReport,
  type Meta,
  type MarketingReport,
  type Me,
  type SalesWeekly,
  type OperationsReport,
  type Overview,
  type ParkGeo,
  type ParkRevenue,
  type JourneyReport,
  type PipelineReport,
  type DigestReport,
  type SavedView,
  previousPeriod,
} from "./lib/api.ts";
import { daysAgoISO, money, monthStartISO, num, pct, pctValue, shortDate, todayISO, weekdayDate } from "./lib/format.ts";
import { ParkMap } from "./components/ParkMap.tsx";
import { Card, ChannelTable, DailyChart, DrillNum, Freshness, Kpi, LeadFunnel, RevenueSplit } from "./components/widgets.tsx";
import { LeadDrawer } from "./components/leadList.tsx";
import { DealDrawer, DrillProvider, type Drill } from "./components/dealList.tsx";
import { ConversationDrawer } from "./components/conversationList.tsx";
import { ExceptionDrawer, PartyCheckDrawer } from "./components/exceptionList.tsx";
import { TicketBookingDrawer } from "./components/ticketSources.tsx";
import { PaymentDrawer } from "./components/depositReview.tsx";
import { GlobalSearch, type SearchNav } from "./components/search.tsx";
import { Th, useSortable } from "./components/sortable.tsx";
import type { DrillDown } from "./components/leadList.tsx";
import { BrandsScreen, ExportScreen, OperationsScreen, PipelineScreen } from "./components/screens.tsx";
import { MarketingScreen } from "./components/marketing.tsx";
import { AdminScreen } from "./components/admin.tsx";
import { SalesScreen } from "./components/sales.tsx";
import { JourneyScreen } from "./components/journey.tsx";
import { ClientBaseScreen } from "./components/clientBase.tsx";
import { B2bDealDrawer, B2bScreen } from "./components/b2b.tsx";
import { DigestScreen } from "./components/digest.tsx";
import { dbLabel, plural, t, useLang, type L } from "./i18n/lang.ts";
import { ERA, NAV_GROUP, SCREEN, SENTINEL, UI, UTM_BASIS } from "./i18n/dict.ts";
import { makeDelta } from "./lib/compare.ts";
import { AppearanceMenu, Icon, ScreenHead, useChrome } from "./components/chrome.tsx";

/**
 * Справочник — отдельный чанк: 52 КБ текста на двух языках не должны
 * задерживать первый экран у всех ради тех, кто заглядывает сюда изредка.
 */
const ReferenceScreen = lazy(() =>
  import("./components/reference.tsx").then((m) => ({ default: m.ReferenceScreen })),
);

type Screen =
  | "digest" | "overview" | "parks" | "brands" | "pipeline" | "operations"
  | "marketing" | "sales" | "export" | "admin" | "metrics" | "journey" | "client_base" | "b2b";

/**
 * Проза этого экрана. Один блок в начале файла, а не строки внутри разметки:
 * подписей здесь около сотни, и вписанные в JSX пары удвоили бы объём разметки.
 * Блок лежит в файле САМОГО экрана, а не в общем словаре, — текст экрана должен
 * жить вместе с экраном, иначе он от него оторвётся при первой же правке.
 */
const TX = {
  /* --- сворачивание панели фильтров на узком экране */
  showFilters: { ru: "Показать фильтры", en: "Show filters" },
  hideFilters: { ru: "Скрыть фильтры", en: "Hide filters" },
  parksCounted: {
    one: { ru: "парк", en: "park" },
    few: { ru: "парка", en: "parks" },
    many: { ru: "парков", en: "parks" },
  },

  /* --- оболочка и вход */
  tagline: { ru: "Сначала цифры. Догадки — никогда.", en: "Numbers first. Guesses never." },
  loginSub: {
    ru: "Маркетинг и продажи по всем паркам, один источник правды.",
    en: "Marketing and sales for every park, one source of truth.",
  },
  login: { ru: "Логин", en: "Login" },
  password: { ru: "Пароль", en: "Password" },
  signIn: { ru: "Войти", en: "Sign in" },
  signingIn: { ru: "Вход…", en: "Signing in…" },
  signInFailed: { ru: "Войти не удалось", en: "Sign-in failed" },
  loginNote: {
    ru: "При открытии из Битрикс24 вход выполняется автоматически. Права проверяются на сервере — скрытый пункт меню это удобство, а не защита.",
    en: "Opened from Bitrix24 you are signed in automatically. Rights are checked on the server — a hidden menu item is convenience, not security.",
  },

  /* --- оговорки про эпохи. Числа сюда не вписаны намеренно: захардкоженная
         цифра под воронкой уже однажды разошлась с данными. */
  legacyWarnTitle: {
    ru: "Всё, что касается движения между стадиями, недостоверно раньше",
    en: "Anything about movement between stages is unreliable before",
  },
  legacyWarnBody: {
    ru: "Все переходы стадий, которые у нас есть, датированы этой датой или позже — истории глубже CRM не хранит. У лидов, созданных раньше, воронка показывает стадию, в которой лид стоял к началу сбора, а не путь, которым шёл: лид, прошедший в 2025-м New → Interested → Closed, виден только в последней стадии. Количество лидов, сделок, выручка и касса парков за тот период настоящие и сравнимые; конверсия между стадиями, время до стадии и форма воронки — нет.",
    en: "Every stage transition we hold is dated on that day or later — the CRM keeps no history further back. For leads created earlier, the funnel shows the stage the lead was standing in when collection began, not the path it took: a lead that went New → Interested → Closed in 2025 appears in its final stage only. Counts of leads, deals, revenue and park takings for that period are real and comparable; conversion between stages, time-to-stage and the funnel shape are not.",
  },
  managedNote: {
    ru: "Выбранный период начинается раньше, но показатели по лидам и сделкам отфильтрованы рабочей эпохой и всё более раннее игнорируют. Полный диапазон покрывает только выручка парков.",
    en: "The selected period starts earlier, but lead and deal metrics are filtered to the managed era, so they ignore anything before it. Only park revenue covers the full range.",
  },
  includeOlder: { ru: "Включить более ранние данные", en: "Include older data" },
  allDataInclLegacy: { ru: "все данные, вместе со старой эпохой", en: "all data incl. legacy" },
  bothErasCycles: { ru: "обе эпохи — циклы идут через обе", en: "both eras — cycles span them" },
  b2bNoEra: { ru: "воронка B2B с 30.10.2026, эпоха не применяется", en: "B2B pipeline since 30 Oct 2026, the era does not apply" },

  /* --- сравнение */
  compareBlockedTitle: {
    ru: "Сравнивать этот период не с чем.",
    en: "There is nothing to compare against for this period.",
  },
  compareBlockedBody1: {
    ru: "Предыдущий отрезок той же длины кончается",
    en: "The previous stretch of the same length ends on",
  },
  compareBlockedBody2: {
    ru: "— раньше, чем начинается рабочая эпоха",
    en: "— before the managed era begins on",
  },
  compareBlockedBody3: {
    ru: "При фильтре «Рабочая эпоха» в нём нет ни одного лида, и сравнение с нулём читалось бы как бурный рост, а не как отсутствие данных. Либо выберите период, начинающийся позже, либо переключите фильтр на «Все данные» и читайте результат, помня, что история стадий так далеко не идёт.",
    en: "Under the Managed era filter it holds no leads at all, and a comparison against zero would read as explosive growth rather than as missing data. Either pick a period that starts later, or switch the filter to All data and read the result knowing that stage history does not go back that far.",
  },

  /* --- фильтр по неделям на экране недельных продаж.
         Подпись самого поля берётся из общего словаря (`UI.week`): это термин, а
         не проза экрана, и второй его копии здесь быть не должно. */
  lastCompleteWeekOption: { ru: "Последняя полная", en: "Last complete" },
  previousWeek: { ru: "Предыдущая неделя", en: "Previous week" },
  nextWeek: { ru: "Следующая неделя", en: "Next week" },
  weekPipelineUnknown: { ru: "пайплайн неизвестен", en: "pipeline unknown" },
  lastCompleteWeekMark: { ru: "последняя полная неделя", en: "last complete week" },
  completeWeekMark: { ru: "полная неделя", en: "complete week" },
  weekInProgressMark: { ru: "неделя не закончилась", en: "week not finished" },
  compareToPreviousWeek: {
    ru: "Сравнить с прошлой неделей",
    en: "Compare to previous week",
  },
  comparingAgainstWeek: {
    ru: "сравнение с предыдущей неделей",
    en: "comparing against the previous week",
  },

  /* --- сохранённые наборы фильтров */
  applyThisView: { ru: "Применить этот набор", en: "Apply this view" },
  deleteThisView: { ru: "Удалить этот набор", en: "Delete this view" },
  saveAsView: { ru: "Сохранить текущие фильтры набором", en: "Save the current filters as a view" },
  nameThisView: { ru: "Название набора", en: "Name this view" },

  /* --- обзор */
  closedAsJunk: { ru: "закрыто как мусор", en: "closed as junk" },
  inPreviousPeriod: { ru: "в предыдущем периоде", en: "in the previous period" },
  qualifiedLeads: { ru: "Квалифицированные лиды", en: "Qualified leads" },
  footQualified: {
    ru: "лидов · побывали на Interested или Park Tour, либо завершились праздником",
    en: "of leads · were at Interested or Park Tour, or ended in a completed party",
  },
  footStraightToDeal: {
    ru: "из них — сразу в сделку, минуя обе стадии",
    en: "of them went straight to a deal, skipping both stages",
  },
  drillStraightToDeal: {
    ru: "Квалифицированы завершённым праздником",
    en: "Qualified by a completed party",
  },
  becameDeal: { ru: "Стали сделкой", en: "Became a deal" },
  footDeal: { ru: "лидов ·", en: "of leads ·" },
  /* Счётное существительное: в русском три формы. */
  completedEvents: {
    one: { ru: "завершённый праздник", en: "completed event" },
    few: { ru: "завершённых праздника", en: "completed events" },
    many: { ru: "завершённых праздников", en: "completed events" },
  },
  banquetRevenueSyrve: { ru: "Выручка с банкетов (Syrve)", en: "Banquet revenue (Syrve)" },
  /*
   * Сноска называет ВТОРОЕ число и объясняет, что с ним делать. Прежний текст
   * «из кассы Syrve · в сделках Битрикса 27,7 тыс.» читался одной фразой —
   * «из кассы Syrve в сделках Битрикса», — и заказчик прямо написал, что не
   * понимает, что это значит. Он прав: первая половина описывала БОЛЬШОЕ число
   * сверху, у которого своей цифры рядом нет, вторая — маленькое, стоящее
   * следом; точка-разделитель такую перемену подлежащего не выдерживает.
   *
   * Откуда взята большая цифра, уже сказано в подписи плитки — «Выручка с
   * банкетов (Syrve)», — поэтому повторять источник в сноске незачем. Зато
   * нужно сказать то, чего на экране нет: складывать эти два числа нельзя.
   */
  footBanquetSameParties: {
    ru: "те же праздники в сделках Битрикса —",
    en: "the same parties in Bitrix deals —",
  },
  footBanquetDoNotAdd: {
    ru: "складывать нельзя, будет двойной счёт",
    en: "do not add the two up — that double-counts",
  },
  /* Плитка банкетной кассы с 04.10.2026 — чеки праздников целиком. */
  partyChecksCounted: {
    one: { ru: "чек праздника", en: "party check" },
    few: { ru: "чека праздников", en: "party checks" },
    many: { ru: "чеков праздников", en: "party checks" },
  },
  hintPartyChecks: {
    ru: "банкетные заказы и заказы с пакетом, по учётному дню Syrve",
    en: "banquet orders and orders with a package, by Syrve business day",
  },
  drillCrmParties: { ru: "Те же праздники в сделках", en: "The same parties in deals" },
  hintCrmParties: {
    ru: "выигранные сделки с праздником в периоде, с исключениями сверки; сумма плитки — их сумма",
    en: "won deals with the party in the period, reconciliation exceptions applied; the tile is their total",
  },
  exceptionsApplied: {
    one: { ru: "исключение сверки", en: "reconciliation exception" },
    few: { ru: "исключения сверки", en: "reconciliation exceptions" },
    many: { ru: "исключений сверки", en: "reconciliation exceptions" },
  },
  drillExceptions: { ru: "Исключения сверки", en: "Reconciliation exceptions" },
  hintExceptions: {
    ru: "записи, по которым праздники периода посчитаны не так, как стоит в CRM",
    en: "records that make this period's parties differ from the CRM",
  },
  leadFunnel: { ru: "Воронка лидов", en: "Lead funnel" },
  leadFunnelHint: {
    ru: "лиды, когда-либо доходившие до каждой стадии",
    en: "leads that ever reached each stage",
  },
  leadsPerDay: { ru: "Обращения по дням", en: "Inquiries per day" },
  sourcesHint: {
    ru: "нажмите на любое число, чтобы увидеть лиды за ним и ссылку в Битрикс",
    en: "click any number to see the leads behind it, with a link into Bitrix",
  },
  pipelineHint: {
    ru: "Interested + Park Tour → сделка, активная воронка",
    en: "Interested + Park Tour → deal, active pipeline",
  },
  colReached: { ru: "Вошли в Interested / Park Tour", en: "Entered Interested / Park Tour" },
  colDeal: { ru: "Сделка", en: "Deal" },
  colCompleted: { ru: "Завершено", en: "Completed" },
  colConv: { ru: "Конв.", en: "Conv." },
  campaignTagging: { ru: "Разметка кампаний", en: "Campaign tagging" },
  campaignTaggingHint: {
    ru: "откуда взялась метка, а не какие поля UTM заполнены",
    en: "where the tag came from, not which UTM fields are filled",
  },
  campaignTaggingNote: {
    ru: "Исторические метки — это в основном названия парков и телефонные пометки, набранные руками. Нормальный сбор UTM — отдельная задача, см. GAPS.md, G-30.",
    en: "Historical tags are mostly park names and telephony markers typed by hand. Proper UTM capture is a separate task — see GAPS.md, G-30.",
  },

  /* --- парки */
  posRevenue: { ru: "Касса", en: "POS revenue" },
  onlineTickets: { ru: "Онлайн-билеты", en: "Online tickets" },
  fromBookingSite: { ru: "с нашего сайта бронирования", en: "from our booking site" },
  ticketsSold: { ru: "Билетов продано", en: "Tickets sold" },
  footTicketsSold: {
    ru: "билетных единиц в кассе · единственный измеримый счётчик посещаемости",
    en: "ticket units at the till · the only visitor count we can measure",
  },
  whereParksAre: { ru: "Где стоят парки", en: "Where the parks are" },
  withoutSyrve: { ru: "без доступа к Syrve", en: "without Syrve access" },
  revenueMix: { ru: "Состав выручки", en: "Revenue mix" },
  posCategories: { ru: "категории кассы", en: "POS categories" },
  revenueByPark: { ru: "Выручка по паркам", en: "Revenue by park" },
  colPosRevenue: { ru: "Касса, AED", en: "POS revenue, AED" },
  colFood: { ru: "Еда", en: "Food" },
  /* Чеки праздников целиком (миграция 090) — то же, что плитка на Обзоре. */
  colBanquet: { ru: "Чеки праздников", en: "Party checks" },
  colOnline: { ru: "Онлайн, AED", en: "Online, AED" },
  noAccess: { ru: "нет доступа", en: "no access" },
  parkRevenueNote: {
    ru: "Касса — источник правды по парку. Банкетные сделки CRM намеренно не прибавляются сверху: банкетные деньги частично проходят через кассу, и сумма дала бы двойной счёт.",
    en: "POS revenue is the source of truth for a park. CRM banquet deals are deliberately not added on top: banquet money partly goes through the till, so summing them would double-count.",
  },

  /* --- детализация под цифрой
     Подпись панели обязана называть ДАТУ ОТБОРА, а не только предмет: все
     плитки этого экрана считают лидов, СОЗДАННЫХ в периоде, а таблица
     «Конверсия воронки» рядом — вошедших в продажи внутри периода. Два разных
     населения, и без даты в подписи два списка выглядели бы одним. */
  drillByLeadCreated: {
    ru: "по дате создания лида",
    en: "by the date the lead was created",
  },
  drillAllLeads: { ru: "все лиды периода", en: "all leads in the period" },
  drillWonLeads: {
    ru: "Лиды с завершённым праздником",
    en: "Leads with a completed event",
  },
  drillOpenList: { ru: "показать список", en: "show the list" },
} satisfies Record<string, L | { one: L; few: L; many: L }>;

/**
 * Кнопка-цифра для детализации ТАМ, ГДЕ ПЛИТКИ НЕТ.
 *
 * Стиль повторяет кнопку внутри `Kpi` (widgets.tsx): пунктирное подчёркивание и
 * полностью наследуемый шрифт. Своя копия, а не импорт, — та константа
 * приватна для своего модуля, и вытаскивать её наружу значило бы править чужой
 * файл ради семи символов. Ни один визуальный параметр цифры не меняется:
 * кнопка не приносит ни своего шрифта, ни своего цвета, ни отступов.
 */
const DRILL_VALUE: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "baseline",
  margin: 0,
  padding: 0,
  border: 0,
  background: "none",
  color: "inherit",
  font: "inherit",
  textAlign: "inherit",
  cursor: "pointer",
  textDecoration: "underline",
  textDecorationStyle: "dotted",
  textUnderlineOffset: 4,
};

/**
 * Корзины разметки, у которых ключ плитки совпадает со срезом списка.
 *
 * Плитки блока «Разметка кампаний» считаются по `basis_key` (пять значений), а
 * список отбирает по `basis` (четыре значения `core.v_lead_source`). На трёх
 * ключах это одно и то же значение и одно и то же число; на двух остальных —
 * нет, подробности у самой плитки.
 */
const DRILLABLE_BASIS = new Set(["client_tag", "analytics_tag", "chat_link", "site_ref"]);

/**
 * ОТМЕНЕНО 10.09.2026. Здесь стояло: «Справочник расчётов — единственный экран
 * на двух языках. Остальной интерфейс английский: это названия колонок и стадий,
 * которые совпадают с CRM, и переводить их значило бы расходиться с системой,
 * откуда они приехали». Заказчик решил иначе: двуязычным становится весь
 * интерфейс.
 *
 * Часть той причины ОСТАЛАСЬ в силе и стала правилом: названия стадий, парков,
 * брендов, менеджеров и кампаний не переводятся ни на каком языке — это зеркало
 * CRM и данные. Переводится то, что написали мы.
 *
 * Прежний текст сохранён ниже как след решения, которое отменили.
 *
 * Остальной интерфейс английский: это названия колонок и стадий, которые
 * совпадают с CRM, и переводить их значило бы расходиться с системой, откуда
 * пришли данные. Справочник — связный текст, и его читают руководители, для
 * части которых английский не рабочий язык. Поэтому переключатель есть здесь
 * и только здесь; заголовок и пункт меню следуют за ним, иначе экран назывался
 * бы не так, как называет себя изнутри.
 */
/* Название и подзаголовок справочника переехали в общий словарь экранов
   (`i18n/dict.ts`, SCREEN.metrics): они печатаются и в меню, и над содержимым, и
   в списке прав, а три копии одного названия однажды разошлись бы. */

/**
 * Состав меню данными, а не разметкой.
 *
 * Пунктов тринадцать, и у каждого были свои четыре строки JSX с одинаковой
 * обвязкой прав и подсветки. Список короче, и добавить экран теперь значит
 * добавить строку, а не скопировать блок и забыть в нём `can()`.
 */
/** Дайджест стоит отдельной группой: его смотрят первым и до планёрки. */
/**
 * Подписи пунктов НЕ дублируются здесь: имя экрана берётся из общего словаря по
 * ключу (`SCREEN[key].title`). До двуязычия название экрана было написано в трёх
 * местах — в меню, над содержимым и в списке прав, — и три копии одного слова
 * означали три места, где его однажды поправят в двух.
 */
const NAV_MORNING: Array<{ key: Screen; icon: string }> = [{ key: "digest", icon: "digest" }];

const NAV_DASHBOARDS: Array<{ key: Screen; icon: string }> = [
  { key: "overview", icon: "overview" },
  { key: "parks", icon: "parks" },
  { key: "brands", icon: "brands" },
  { key: "pipeline", icon: "pipeline" },
  { key: "operations", icon: "deals" },
  { key: "marketing", icon: "marketing" },
  { key: "sales", icon: "sales" },
  /* Воронка B2B — отдельный продукт со своим экраном, в банкетные цифры не входит. */
  { key: "b2b", icon: "b2b" },
];

const NAV_CUSTOMERS: Array<{ key: Screen; icon: string }> = [
  { key: "journey", icon: "journey" },
  { key: "client_base", icon: "clients" },
];

/** Итоги по паркам. Одно выражение на текущий период и на прошлый. */
function sumParks(parks: ParkRevenue["parks"] | undefined) {
  return (parks ?? []).reduce(
    (acc, p) => ({
      revenue: acc.revenue + Number(p.revenue_aed_minor),
      tickets: acc.tickets + Number(p.tickets_aed_minor),
      food: acc.food + Number(p.food_aed_minor),
      banquet: acc.banquet + Number(p.banquet_aed_minor),
      partyChecks: acc.partyChecks + Number(p.party_checks ?? 0),
      banquetCategory: acc.banquetCategory + Number(p.banquet_category_aed_minor ?? 0),
      other: acc.other + Number(p.other_aed_minor),
      ticketsSold: acc.ticketsSold + Math.round(Number(p.tickets_sold)),
      online: acc.online + Number(p.online_revenue_aed_minor),
    }),
    { revenue: 0, tickets: 0, food: 0, banquet: 0, partyChecks: 0, banquetCategory: 0, other: 0, ticketsSold: 0, online: 0 },
  );
}

/**
 * Готовые периоды. «7d»/«30d» — не подпись, а обозначение, одинаковое на любом
 * языке; переводить его в «7д» значило бы менять ширину кнопки ради нуля смысла.
 * Третий пункт — фраза, и он в словаре.
 */
const PRESETS: Array<{ key: string; label: L | null; from: () => string }> = [
  { key: "7d", label: null, from: () => daysAgoISO(7) },
  { key: "30d", label: null, from: () => daysAgoISO(30) },
  /* Период по умолчанию — с начала текущего месяца; кнопка нужна, чтобы к нему
     можно было вернуться одним щелчком и видеть, что выбран именно он. */
  { key: "mtd", label: UI.monthToDate, from: monthStartISO },
  { key: "jul", label: UI.since1Jul, from: () => "2026-07-01" },
];

/**
 * Экраны, у которых фильтра периода НЕТ.
 *
 * Дайджест — про сутки, и период к нему не применяется. Недельные продажи — про
 * одну неделю: замерено 10.09.2026, что из семи блоков того экрана период менял
 * два, а пять игнорировал, и что период не резал неделю (01.09→03.09 отдавал
 * полную неделю 2026-08-31). Показывать переключатель, который ничего не делает,
 * — обещать управление, которого нет; поэтому сначала снимаются ОРГАНЫ, а потом
 * подпись, а не наоборот.
 */
const NO_PERIOD: Screen[] = ["metrics", "digest", "sales"];

/**
 * Подпись свёрнутой панели фильтров.
 *
 * Свёрнутая панель обязана называть то, что прячет: числа под ней посчитаны по
 * этим фильтрам, и панель, молчащая о выбранном парке, врёт о них. Поэтому в
 * свёрнутом виде кнопка показывает не слово «Фильтры», а сам отбор — парк и
 * период.
 *
 * Функция модульная и без хуков: она зовётся в разметке ПОСЛЕ раннего возврата
 * на экран входа, и любой хук здесь уронил бы всё дерево.
 */
function filterSummary(
  parkNames: string[],
  parkTotal: number,
  period: string | null,
  legacy: boolean,
  comparing: boolean,
): string {
  /* Пока справочник не приехал, парков не «Все 0», а неизвестно сколько:
     печатать здесь ноль значило бы соврать на первом же кадре. */
  const parks =
    parkNames.length === 0
      ? parkTotal === 0
        ? null
        : `${t(UI.all)} ${parkTotal}`
      : parkNames.length === 1
        ? (parkNames[0] ?? "")
        : `${parkNames.length} ${plural(parkNames.length, TX.parksCounted)}`;
  /* Эпоха и сравнение попадают в сводку ТОЛЬКО когда включены, и это не
     экономия места: оба переписывают каждое число под свёрнутой панелью, а
     органов управления ими в свёрнутом виде не видно. */
  return [parks, period, legacy ? t(UI.allData) : null, comparing ? t(UI.compareOn) : null]
    .filter((s) => s !== null && s !== "")
    .join(" · ");
}

/**
 * Выбранная неделя недельного отчёта.
 *
 * Приезжает полем `week` в ответе `salesWeekly`, и это единственный источник
 * правды о выборе: неделю разрешает СЕРВЕР (текущая неполная и будущая
 * подтягиваются к последней полной), и вычислять её на экране заново значило бы
 * иметь два ответа на один вопрос.
 *
 * Приведение типа, а не поле в типе, — потому что `SalesWeekly` объявлен в
 * `lib/api.ts`, и добавить туда колонку должен владелец файла. Тот же приём уже
 * стоит в `components/sales.tsx` для `deposits_known`.
 * TODO: когда `lib/api.ts` объявит `week` и `inboundQueue`, приведения убрать.
 */
type SalesWeekMeta = {
  start: string;
  end: string;
  pipeline_known: boolean;
  is_last_complete: boolean;
  is_current: boolean;
  has_weeks: boolean;
};

function salesWeekMeta(r: SalesWeekly | null): SalesWeekMeta | null {
  return (r as (SalesWeekly & { week?: SalesWeekMeta }) | null)?.week ?? null;
}

/** Пункт селектора недель: только завершённые недели, признак истории рядом. */
type WeekOption = { start: string; end: string; pipelineKnown: boolean };

function weekOptions(r: SalesWeekly | null): WeekOption[] {
  return (r?.weeks ?? [])
    .filter((w) => !w.is_current)
    .map((w) => ({ start: w.week_start, end: w.week_end, pipelineKnown: w.pipeline_known }));
}

export default function App(): React.ReactElement {
  const [screen, setScreen] = useState<Screen>("overview");
  /* По умолчанию — с 1-го числа текущего месяца: руководители смотрят месяц,
     а «с 1 июля» растягивалось с каждым месяцем и смешивало старые с новыми. */
  const [from, setFrom] = useState(monthStartISO);
  const [to, setTo] = useState(todayISO());
  const [parkFilter, setParkFilter] = useState<string[]>([]);
  /**
   * Фильтр по брендам — отдельный от парков.
   *
   * Бренд не равен набору своих парков: к бренду привязано и то, у чего парка
   * нет вовсе — реклама бренда, лид, которому парк ещё не проставили. Сервер
   * различал это всегда, задать бренд было нечем.
   */
  const [brandFilter, setBrandFilter] = useState<string[]>([]);
  /**
   * Эпоха данных. Раньше была жёстко "managed", и из-за этого выбор даты раньше
   * 1 июля не менял ничего, кроме выручки парков: лиды и сделки продолжали
   * фильтроваться по managed. Теперь это явный переключатель, и при выходе за
   * границу эпох интерфейс сам предлагает включить старые данные.
   */
  const [era, setEra] = useState<"managed" | "all">("managed");
  /**
   * Выбранная неделя недельного отчёта. `null` — «последняя полная», и это
   * ЛИТЕРАЛ, а не дата: утренний чип руководителя обязан ехать вместе со
   * временем, а не примерзать к неделе, в которую его сохранили.
   *
   * Отдельно от `from`/`to` намеренно: период остаётся у остальных экранов
   * нетронутым. Иначе заход на недельные продажи молча схлопывал бы выбранные
   * тридцать дней в одну неделю у всего приложения.
   */
  const [salesWeek, setSalesWeek] = useState<string | null>(null);
  /**
   * Одна подписка на язык, в корне. Смена языка перерисовывает всё дерево:
   * язык у документа один и меняется целиком, а полупереведённый экран
   * выглядел бы как поломка, а не как настройка.
   */
  useLang();

  const [meta, setMeta] = useState<Meta | null>(null);
  const [geo, setGeo] = useState<ParkGeo[]>([]);
  const [overview, setOverview] = useState<Overview | null>(null);
  const [revenue, setRevenue] = useState<ParkRevenue | null>(null);
  const [brands, setBrands] = useState<BrandReport | null>(null);
  const [pipeline, setPipeline] = useState<PipelineReport | null>(null);
  const [ops, setOps] = useState<OperationsReport | null>(null);
  const [mkt, setMkt] = useState<MarketingReport | null>(null);
  const [me, setMe] = useState<Me | null>(null);
  const [sales, setSales] = useState<SalesWeekly | null>(null);
  const [journey, setJourney] = useState<JourneyReport | null>(null);
  const [screenBusy, setScreenBusy] = useState(false);
  /**
   * Какая цифра сейчас раскрыта в список. Живёт в App, а не внутри таблицы:
   * панель одна на все экраны, и держать её состояние в каждой таблице значило
   * бы уметь открыть две сразу.
   */
  /**
   * Тема, плотность строк и свёрнутое меню. Живут на `<html>`, потому что
   * панель со списком лидов рисуется поверх страницы, а правила печати смотрят
   * на корень документа.
   */
  const chrome = useChrome();
  /**
   * Сравнение с предыдущим периодом.
   *
   * Выключено по умолчанию намеренно: включённое, оно удваивает число запросов
   * на каждый экран, а нужно оно не всегда. Состояние не запоминается между
   * сессиями — иначе человек однажды увидел бы вдвое медленный дашборд и не
   * понял, почему.
   */
  /**
   * Сохранённые наборы фильтров.
   *
   * Смысл: руководитель отдела продаж каждое утро выставляет один и тот же
   * набор руками. Один чип вместо пяти нажатий.
   *
   * `naming` — имя, которое человек вводит прямо в панели. Отдельного окна нет
   * намеренно: сохранение занимает одно поле, и модальное окно ради одного поля
   * стоит дороже, чем экономит.
   */
  const [views, setViews] = useState<SavedView[]>([]);
  const [naming, setNaming] = useState<string | null>(null);
  const [compare, setCompare] = useState(false);
  const [prevOverview, setPrevOverview] = useState<Overview | null>(null);
  /**
   * Прошлый период ТЕКУЩЕГО экрана. Один слот, а не по одному на экран: за раз
   * открыт один экран, и держать шесть слотов значило бы шесть путей, по которым
   * в них попадают устаревшие данные.
   *
   * Ключ экрана лежит рядом со значением: без него после переключения экрана
   * плитки успевали бы взять сравнение от предыдущего.
   */
  const [prevScreen, setPrevScreen] = useState<{ key: Screen; data: unknown } | null>(null);
  const [digestData, setDigestData] = useState<DigestReport | null>(null);
  /**
   * Детализация под метрикой: один слот на лиды И на сделки.
   *
   * Два слота означали бы две панели, способные открыться одновременно, и
   * вопрос «какая из них про то число, на которое я нажал».
   */
  const [drill, setDrill] = useState<Drill | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [needsLogin, setNeedsLogin] = useState(false);

  const filters = useMemo(
    () => ({ from, to, era, park: parkFilter, brand: brandFilter }),
    [from, to, era, parkFilter, brandFilter],
  );

  /**
   * Фильтры недельного отчёта: те же парки, но вместо периода — один день
   * выбранной недели.
   *
   * Эндпоинт берёт неделю понедельником этого дня, а `to` и `era` не читает.
   * Когда неделя не выбрана, посылается сегодняшний день, и сервер сам
   * подтягивает выбор к последней ПОЛНОЙ неделе: у текущей неполной конверсия
   * 0,9 % против 8,2 % у полной, и по умолчанию она не говорит ни о чём.
   */
  const salesFilters = useMemo(() => {
    const day = salesWeek ?? todayISO();
    return { ...filters, from: day, to: day };
  }, [filters, salesWeek]);

  /**
   * Предыдущий период может целиком лежать ДО границы эпох, и тогда сравнивать
   * не с чем: под фильтром `managed` там ноль по построению, а не по факту.
   *
   * Замер: период 01.07-09.09 даёт предыдущий отрезок 21.04-30.06, и плитки
   * показывали «▲ +9 288 из нуля» — это читается как взрывной рост. Такое
   * сравнение хуже отсутствия сравнения, поэтому здесь оно не показывается
   * вовсе, а вместо значков появляется объяснение.
   */
  const prevRange = previousPeriod(filters);
  const historyFrom = overview?.leads.historyFrom ?? null;
  const compareBlocked =
    compare && era === "managed" && historyFrom !== null && prevRange.to < historyFrom;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [who, m, g, o, r] = await Promise.all([
        api.me(),
        api.meta(),
        api.parksGeo(),
        api.overview(filters),
        api.parkRevenue(filters),
      ]);
      setMe(who);
      setMeta(m);
      setGeo(g.parks);
      setOverview(o);
      setRevenue(r);
      setNeedsLogin(false);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) setNeedsLogin(true);
      else setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    void load();
  }, [load]);

  /* Виды грузятся один раз: они не зависят ни от периода, ни от экрана. */
  useEffect(() => {
    void api
      .views()
      .then((r) => setViews(r.views))
      .catch(() => setViews([]));
  }, []);

  /**
   * Предыдущий период грузится ОТДЕЛЬНЫМ запросом и только когда сравнение
   * включено. Складывать его в основной запрос значило бы платить за второй
   * отчёт всегда, включая тех, кто сравнение не открывает.
   *
   * Ошибка сравнения гасится молча: это добавка к числу, и если она не пришла,
   * плитка должна остаться без значка, а не превратить экран в сообщение об
   * ошибке.
   */
  useEffect(() => {
    if (!compare || compareBlocked) {
      setPrevOverview(null);
      return;
    }
    let cancelled = false;
    void api
      .overview(previousPeriod(filters))
      .then((o) => {
        if (!cancelled) setPrevOverview(o);
      })
      .catch(() => {
        if (!cancelled) setPrevOverview(null);
      });
    return () => {
      cancelled = true;
    };
  }, [compare, filters]);

  /**
   * Прошлый период экрана. Тот же запрос, что и у текущего, — отличается только
   * диапазон дат: сравнение обязано считаться теми же определениями, иначе
   * разница будет говорить не о времени.
   */
  useEffect(() => {
    const HAS_COMPARE: Screen[] = ["pipeline", "operations", "marketing", "parks"];
    if (!compare || compareBlocked || !HAS_COMPARE.includes(screen)) {
      setPrevScreen(null);
      return;
    }
    let cancelled = false;
    const pf = previousPeriod(filters);
    const call =
      screen === "pipeline" ? api.pipeline(pf)
      : screen === "marketing" ? api.marketing(pf)
      : screen === "parks" ? api.parkRevenue(pf)
      : api.operations(pf);

    void call
      .then((data) => {
        if (!cancelled) setPrevScreen({ key: screen, data });
      })
      .catch(() => {
        /* Сравнение — добавка к числу: не пришло, значит плитки просто без
           значков. Превращать экран в сообщение об ошибке из-за добавки нельзя. */
        if (!cancelled) setPrevScreen(null);
      });
    return () => {
      cancelled = true;
    };
  }, [compare, compareBlocked, screen, filters]);

  /** Дайджест: период с панели к нему не применяется, только парки. */
  useEffect(() => {
    if (screen !== "digest" || digestData !== null) return;
    let cancelled = false;
    setScreenBusy(true);
    void api
      .digest(parkFilter)
      .then((d) => {
        if (!cancelled) setDigestData(d);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setScreenBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [screen, digestData, parkFilter]);

  /**
   * Данные отдельных экранов грузятся по требованию.
   *
   * Тянуть их вместе с общим запросом означало бы платить за четыре тяжёлых
   * отчёта при каждом открытии дашборда, из которых человек посмотрит один.
   * Первый экран должен появляться быстро — это и есть причина, по которой у
   * каждого экрана один запрос, а не двенадцать.
   */
  useEffect(() => {
    let cancelled = false;
    const need =
      (screen === "brands" && !brands) ||
      (screen === "pipeline" && !pipeline) ||
      (screen === "operations" && !ops) ||
      (screen === "marketing" && !mkt) ||
      (screen === "sales" && !sales) ||
      (screen === "journey" && !journey);
    if (!need) return;

    setScreenBusy(true);
    const call =
      screen === "brands" ? api.brands(filters)
      : screen === "pipeline" ? api.pipeline(filters)
      : screen === "marketing" ? api.marketing(filters)
      : screen === "sales" ? api.salesWeekly(salesFilters)
      : screen === "journey" ? api.journey(filters)
      : api.operations(filters);

    void call
      .then((data) => {
        if (cancelled) return;
        if (screen === "brands") setBrands(data as BrandReport);
        else if (screen === "pipeline") setPipeline(data as PipelineReport);
        else if (screen === "marketing") setMkt(data as MarketingReport);
        else if (screen === "sales") setSales(data as SalesWeekly);
        else if (screen === "journey") setJourney(data as JourneyReport);
        else setOps(data as OperationsReport);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setScreenBusy(false);
      });

    return () => {
      cancelled = true;
    };
  }, [screen, filters, salesFilters, brands, pipeline, ops, mkt, sales, journey]);

  /**
   * Смена недели обесценивает загруженный недельный отчёт — тот же приём, что
   * ниже для периода. Без этого экран остался бы с числами прошлой недели, а
   * селектор показывал бы новую: ровно тот дефект, от которого этот файл
   * лечится эффектом на `filters`.
   */
  useEffect(() => {
    setSales(null);
  }, [salesWeek]);

  // Смена фильтров обесценивает уже загруженные экраны — иначе на экране
  // остались бы цифры за прошлый период, а заголовок показывал бы новый.
  //
  // Customer journey тут когда-то забыли, и получилось ровно то, от чего этот
  // эффект и существует: после первой загрузки экран не перезапрашивался
  // никогда — ни при смене периода, ни при клике по парку. Шапка честно
  // печатала новый период, числа оставались от прошлого.
  useEffect(() => {
    setBrands(null);
    setPipeline(null);
    setOps(null);
    setMkt(null);
    setSales(null);
    setJourney(null);
  }, [filters]);

  /* Дайджест зависит только от выбора парков: период к нему не применяется. */
  useEffect(() => {
    setDigestData(null);
  }, [parkFilter]);

  /**
   * Список недель для селектора помнится между загрузками.
   *
   * Иначе на время перезапроса `sales` равен null, селектор исчезает — и
   * стрелка «предыдущая неделя» пропадает из-под курсора ровно в тот момент,
   * когда её нажимают второй раз.
   */
  const [weekMenu, setWeekMenu] = useState<WeekOption[]>([]);
  useEffect(() => {
    const opts = weekOptions(sales);
    if (opts.length > 0) setWeekMenu(opts);
  }, [sales]);

  /**
   * Применение вида ставит РОВНО то, что в нём записано, и ничего не додумывает.
   * Отсутствующее поле оставляется как есть, а не сбрасывается в значение по
   * умолчанию: вид «только Бахрейн» не должен заодно менять период.
   */
  const applyView = (v: SavedView): void => {
    const f = v.filters;
    if (typeof f.from === "string") setFrom(f.from);
    if (typeof f.to === "string") setTo(f.to);
    if (f.era === "managed" || f.era === "all") setEra(f.era);
    if (Array.isArray(f.park)) setParkFilter(f.park);
    if (typeof f.compare === "boolean") setCompare(f.compare);
    /**
     * Неделя недельного отчёта хранится либо датой-понедельником, либо литералом
     * `last_complete`. Литерал приводится к `null` — то есть к «спроси у
     * сервера»: сохранённый как дата, он завтра показывал бы прошлое.
     *
     * Приведение типа — потому что состав `SavedView.filters` объявлен в
     * `lib/api.ts`; сервер это поле уже принимает и отдаёт
     * (`server/src/api/savedViews.ts`, `cleanFilters`).
     * TODO: объявить `week?: string` в `SavedView.filters` и убрать приведение.
     */
    const week = (f as { week?: unknown }).week;
    if (week === "last_complete") setSalesWeek(null);
    else if (typeof week === "string") setSalesWeek(week);
    if (typeof f.screen === "string") setScreen(f.screen as Screen);
  };

  const saveCurrentView = async (name: string): Promise<void> => {
    const trimmed = name.trim();
    if (trimmed === "") return;
    /* Цвет метки берётся по кругу фирменной последовательности: он нужен, чтобы
       чип узнавали быстрее, чем читают, и выбирать его руками не за что. */
    const TINTS = ["yellow", "sky", "pink", "violet", "orange"] as const;
    const tint = TINTS[views.length % TINTS.length] ?? "sky";
    try {
      const r = await api.saveView({
        name: trimmed,
        tint,
        filters: {
          from,
          to,
          era,
          park: parkFilter,
          screen,
          compare,
          /* Не выбранная неделя сохраняется ЛИТЕРАЛОМ, а не сегодняшней датой:
             утренний чип обязан ехать вместе со временем. Ту же оговорку про
             пресет периода см. в server/src/api/savedViews.ts. */
          week: salesWeek ?? "last_complete",
        },
      });
      /* Сохранение под существующим именем ОБНОВЛЯЕТ вид, поэтому старую
         одноимённую запись надо убрать, а не добавить рядом вторую. */
      setViews((cur) => [...cur.filter((v) => v.name !== r.view.name), r.view]);
      setNaming(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const removeView = async (v: SavedView): Promise<void> => {
    try {
      await api.deleteView(v.id);
      setViews((cur) => cur.filter((x) => x.id !== v.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  /*
   * Сортировка двух таблиц этого файла. Хуки объявлены ЗДЕСЬ, выше раннего
   * выхода на экран входа: порядок вызова хуков не может зависеть от того,
   * вошёл человек или нет. До загрузки отчёта массив пустой — это и есть пустая
   * таблица, а серверный порядок парков (`ORDER BY p.sort`) сохраняется, пока
   * ни один заголовок не нажат.
   */
  const Sconv = useSortable(overview?.pipelineConversion ?? [], {
    park: { kind: "text", get: (r) => dbLabel(SENTINEL, r.park_name, r.park_name) },
    reached: { kind: "number", get: (r) => r.reached_handoff },
    deal: { kind: "number", get: (r) => r.with_deal },
    completed: { kind: "number", get: (r) => r.won_deal },
    /* Конверсия считается на сервере, но NULL в ней означает «делить не на
       что», и ячейка печатает прочерк — такие парки уходят в конец, а не
       становятся нулевой конверсией. */
    conv: { kind: "number", get: (r) => r.conv_to_deal_pct },
  });

  /* Тот же признак, что и в ячейках таблицы выручки: нулевая касса означает не
     ноль, а отсутствие доступа к Syrve по этому парку — колонка так и печатает
     «нет доступа», а остальные прочерк. Ноль вместо `null` поставил бы такой
     парк между настоящими нулями, и отсутствие доступа читалось бы как
     измеренный ноль. */
  const noPos = (p: ParkRevenue["parks"][number]): boolean => Number(p.revenue_aed_minor) === 0;

  const Srev = useSortable(revenue?.parks ?? [], {
    park: {
      kind: "text",
      get: (p) => meta?.parks.find((m) => m.park_key === p.park_key)?.name ?? p.park_key,
    },
    revenue: { kind: "number", get: (p) => (noPos(p) ? null : p.revenue_aed_minor) },
    tickets: { kind: "number", get: (p) => (noPos(p) ? null : p.tickets_aed_minor) },
    food: { kind: "number", get: (p) => (noPos(p) ? null : p.food_aed_minor) },
    banquet: { kind: "number", get: (p) => (noPos(p) ? null : p.banquet_aed_minor) },
    ticketsSold: { kind: "number", get: (p) => (noPos(p) ? null : p.tickets_sold) },
    /* Онлайн приходит не из кассы, а с сайта бронирования, и ячейка печатает
       его даже у парка без доступа к Syrve — значит, и сортировать его есть
       чем всегда. */
    online: { kind: "number", get: (p) => p.online_revenue_aed_minor },
  });

  if (needsLogin) return <Login onDone={() => void load()} />;

  /**
   * Пункт меню скрывается, если экран недоступен.
   *
   * Это удобство, а не защита: сервер проверяет права сам на каждом запросе,
   * потому что спрятанная кнопка не мешает открыть адрес руками.
   */
  const can = (key: string): boolean => !me || me.isAdmin || me.screens.includes(key);

  /**
   * Что поиск умеет сделать с приложением. Три действия, больше не нужно.
   *
   * `goScreen` принимает строку, а не тип экрана: тип живёт в этом файле, и
   * поиск о нём знать не должен — приведение делается здесь, в одном месте.
   * Иначе словарь показателей пришлось бы связать с перечислением экранов, и
   * добавление экрана ломало бы поиск.
   *
   * БЕЗ `useMemo`, и это не небрежность. Этот блок стоит ПОСЛЕ раннего возврата
   * на экран входа, а хук после раннего возврата означает, что на одном рендере
   * хуков меньше, чем на предыдущем: React валит всё дерево с «Rendered fewer
   * hooks than expected», и экран становится пустым. Мемоизация здесь и не
   * нужна — объект собран из сеттеров `useState`, а они стабильны сами.
   */
  const searchNav: SearchNav = {
    goScreen: (key: string) => setScreen(key as Screen),
    setParks: setParkFilter,
    openLeads: setDrill,
  };

  /**
   * Какая неделя показана на недельном отчёте. Берётся из ОТВЕТА, а не из
   * состояния: сервер подтягивает текущую неполную и будущую неделю к последней
   * полной, и знать об этом должен один — тот, кто выбор разрешает.
   */
  const salesMeta = salesWeekMeta(sales);
  const salesSelectedWeek = salesMeta?.start ?? salesWeek;
  const weekIndex =
    salesSelectedWeek === null ? -1 : weekMenu.findIndex((w) => w.start === salesSelectedWeek);

  /** Шаг по списку недель: `-1` — предыдущая (список идёт от свежей к старой). */
  const weekStep = (delta: number): string | null => weekMenu[weekIndex - delta]?.start ?? null;

  /**
   * Предыдущая КАЛЕНДАРНАЯ неделя выбранной — для подписи чипа сравнения.
   * Именно календарная, а не сосед по списку: подпись обещает «прошлую неделю»,
   * и если ровно семи дней назад строки нет, обещать нечего.
   */
  const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
  const salesPrevWeek =
    salesSelectedWeek === null
      ? null
      : (weekMenu.find(
          (w) =>
            Date.parse(`${w.start}T00:00:00Z`) ===
            Date.parse(`${salesSelectedWeek}T00:00:00Z`) - WEEK_MS,
        ) ?? null);

  const leadKpi = overview?.leads.kpi ?? {};

  const prevLeadKpi = compareBlocked ? null : (prevOverview?.leads.kpi ?? null);

  /**
   * Функция сравнения для экранов. Отдаёт `undefined`, когда прошлого периода
   * нет или он от другого экрана, — плитка тогда просто без значка.
   */
  const screenDelta = makeDelta(
    screen === "pipeline" ? pipeline
      : screen === "operations" ? ops
      : screen === "marketing" ? mkt
      : screen === "parks" ? revenue
      : null,
    prevScreen !== null && prevScreen.key === screen ? prevScreen.data : null,
  );

  /**
   * Сравнение для плитки. Возвращает `undefined`, когда сравнение выключено или
   * предыдущий период не пришёл, — плитка тогда просто не показывает значок.
   *
   * `inverse` для величин, у которых рост — плохая новость: закрытых как мусор
   * лидов стало больше, и зелёная стрелка вверх соврала бы.
   */
  const delta = makeDelta(leadKpi, prevLeadKpi);
  const dealKpi = overview?.deals.kpi ?? {};
  const display = overview?.deals.display ?? { currency: "AED", minorUnits: 100, mixed: true };

  const totals = sumParks(revenue?.parks);

  /**
   * Итоги прошлого периода той же свёрткой.
   *
   * Плитки этого экрана считаются на клиенте, поэтому общий механизм сравнения
   * по пути в ответе тут не работает. Свёртка вынесена в функцию именно затем,
   * чтобы прошлый период считался ТЕМ ЖЕ выражением: две копии сложения однажды
   * разошлись бы, и сравнение начало бы говорить о разнице формул, а не периодов.
   */
  const prevParks =
    prevScreen !== null && prevScreen.key === "parks"
      ? sumParks((prevScreen.data as ParkRevenue | null)?.parks)
      : null;

  const parkDelta = (key: keyof ReturnType<typeof sumParks>, kind: "money" | "count") =>
    prevParks === null ? undefined : { value: totals[key], prev: prevParks[key], kind };

  const parksWithoutData = (revenue?.parks ?? []).filter(
    (p) => Number(p.revenue_aed_minor) === 0,
  ).length;

  /**
   * Есть ли на этом экране что сворачивать.
   *
   * Два слагаемых, потому что панель состоит из двух независимых частей:
   * органы периода в первой строке (их нет у дайджеста и справочника) и парки с
   * сохранёнными видами во второй (их нет у справочника). Кнопка появляется,
   * если есть хотя бы одна.
   */
  const filtersToCollapse =
    !NO_PERIOD.includes(screen) || screen === "sales" || (meta !== null && screen !== "metrics");

  /**
   * Период для подписи свёрнутой панели. У дайджеста его нет вовсе — экран про
   * сутки, и писать над ним выбранный период было бы ложью.
   */
  const periodSummary =
    screen === "sales"
      ? /* Неделю разрешает сервер, и подпись обязана называть ту, что он
           вернул: `salesWeek` — это ЗАПРОС, и при значении по умолчанию он
           пуст, хотя экран уже показывает конкретную неделю. */
        salesSelectedWeek === null
        ? t(TX.lastCompleteWeekOption)
        : shortDate(salesSelectedWeek)
      : NO_PERIOD.includes(screen)
        ? null
        : `${shortDate(from)} → ${shortDate(to)}`;

  return (
    /*
     * Открывать детализацию умеет любая плитка на любом экране, и прокидывать
     * обработчик через восемь уровней разметки значило бы восемь пропов,
     * которые существуют только чтобы передать один и тот же вызов.
     */
    <DrillProvider open={setDrill}>
    <div className="app">
      <aside className="sidebar">
        {/*
          Знак, вордмарк и фирменный фриз. Буква Z в жёлтом квадрате остаётся и
          в свёрнутом меню — по ней узнают систему, когда подписи скрыты.
        */}
        <div className="logo">
          <div className="logo-mark">Z</div>
          <div className="logo-text side-hide">
            <img src="/brand/logo/zamania-wordmark-white.png" alt="Zamania" />
            <span>{t(UI.analytics)}</span>
          </div>
        </div>

        <div className="frieze" aria-hidden="true" />

        <nav className="side-nav">
          {can("digest") && (
            <div className="nav-group">
              <div className="nav-label">{t(NAV_GROUP.morning)}</div>
              {NAV_MORNING.map((n) => (
                <button
                  key={n.key}
                  type="button"
                  className="nav-item"
                  title={t(SCREEN[n.key].title)}
                  aria-current={screen === n.key ? "page" : undefined}
                  onClick={() => setScreen(n.key)}
                >
                  <span className="ico">
                    <Icon name={n.icon} />
                  </span>
                  <span className="side-hide">{t(SCREEN[n.key].title)}</span>
                </button>
              ))}
            </div>
          )}

          <div className="nav-group">
            <div className="nav-label">{t(NAV_GROUP.dashboards)}</div>
            {NAV_DASHBOARDS.filter((n) => n.key === "overview" || can(n.key)).map((n) => (
              <button
                key={n.key}
                type="button"
                className="nav-item"
                title={t(SCREEN[n.key].title)}
                aria-current={screen === n.key ? "page" : undefined}
                onClick={() => setScreen(n.key)}
              >
                <span className="ico">
                  <Icon name={n.icon} />
                </span>
                <span className="side-hide">{t(SCREEN[n.key].title)}</span>
              </button>
            ))}
          </div>

          {(can("journey") || can("client_base")) && (
            <div className="nav-group">
              <div className="nav-label">{t(NAV_GROUP.customers)}</div>
              {NAV_CUSTOMERS.filter((n) => can(n.key)).map((n) => (
              <button
                key={n.key}
                type="button"
                className="nav-item"
                title={t(SCREEN[n.key].title)}
                aria-current={screen === n.key ? "page" : undefined}
                onClick={() => setScreen(n.key)}
              >
                <span className="ico">
                  <Icon name={n.icon} />
                </span>
                <span className="side-hide">{t(SCREEN[n.key].title)}</span>
              </button>
              ))}
            </div>
          )}

          <div className="nav-group">
            <div className="nav-label">{t(NAV_GROUP.reference)}</div>
            {can("metrics") && (
              <button
                type="button"
                className="nav-item"
                title={t(SCREEN.metrics.title)}
                aria-current={screen === "metrics" ? "page" : undefined}
                onClick={() => setScreen("metrics")}
              >
                <span className="ico">
                  <Icon name="metrics" />
                </span>
                <span className="side-hide">{t(SCREEN.metrics.title)}</span>
              </button>
            )}
            {can("export") && (
              <button
                type="button"
                className="nav-item"
                title={t(SCREEN.export.title)}
                aria-current={screen === "export" ? "page" : undefined}
                onClick={() => setScreen("export")}
              >
                <span className="ico">
                  <Icon name="export" />
                </span>
                <span className="side-hide">{t(SCREEN.export.title)}</span>
              </button>
            )}
            {me?.isAdmin && (
              <button
                type="button"
                className="nav-item"
                title={t(SCREEN.admin.title)}
                aria-current={screen === "admin" ? "page" : undefined}
                onClick={() => setScreen("admin")}
              >
                <span className="ico">
                  <Icon name="admin" />
                </span>
                <span className="side-hide">{t(SCREEN.admin.title)}</span>
              </button>
            )}
          </div>
        </nav>

        {overview && <Freshness rows={overview.freshness} />}

        <button
          type="button"
          className="side-collapse"
          onClick={chrome.toggleMini}
          title={chrome.mini ? t(UI.expandSidebar) : t(UI.collapseSidebar)}
          aria-label={chrome.mini ? t(UI.expandSidebar) : t(UI.collapseSidebar)}
        >
          <Icon name="collapse" size={15} width={2.4} />
          <span className="side-hide">{t(UI.collapse)}</span>
        </button>
      </aside>

      <div className="shell">
        {/*
          Верхняя панель липкая и без заголовка: в дизайне она несёт только
          фильтры, а название экрана стоит над содержимым. Так при прокрутке
          длинной таблицы фильтры остаются под рукой, а место сверху не тратится
          на слово, которое и так подсвечено в меню.

          На узком экране она сворачивается — см. кнопку ниже.
        */}
        <header className="topbar noprint">
          <div className="topbar-row">
            {/*
              Кнопка «Фильтры». Видна только на узком экране: правило стоит под
              медиа-запросом в theme.css, на широком сворачивать нечего.

              Показывается лишь тогда, когда есть что сворачивать: на справочнике
              показателей фильтров нет вовсе, и кнопка там обещала бы управление,
              которого нет.
            */}
            {filtersToCollapse && (
              <button
                type="button"
                className="chip-btn filters-toggle"
                aria-expanded={chrome.filtersOpen}
                title={chrome.filtersOpen ? t(TX.hideFilters) : t(TX.showFilters)}
                onClick={chrome.toggleFilters}
              >
                {/* Содержимое ПОСТОЯННОЕ, без подмены подписи по состоянию:
                    свёрнутая подпись длиннее развёрнутой на целое название
                    парка, и кнопка меняла бы ширину под пальцем. Состояние
                    несёт `aria-expanded`, а не текст. */}
                <Icon name="collapse" size={12} width={2.4} />
                <b>{t(UI.filters)}</b>
                <span className="sum">
                  {filterSummary(
                    meta === null
                      ? []
                      : meta.parks
                          .filter((pk) => parkFilter.includes(pk.park_key))
                          .map((pk) => pk.name),
                    meta?.parks.length ?? 0,
                    periodSummary,
                    era === "all",
                    compare,
                  )}
                </span>
              </button>
            )}
            {/*
              На дайджесте органов управления периодом нет: он про сутки, и
              фильтр периода к нему не применяется. Показывать переключатель,
              который ничего не делает, — обещать управление, которого нет.
            */}
            {!NO_PERIOD.includes(screen) && (
              <div className="topbar-filters">
                <div className="seg" role="group" aria-label={t(UI.periodPreset)}>
                  {PRESETS.map((pr) => (
                    <button
                      key={pr.key}
                      type="button"
                      aria-selected={from === pr.from() && to === todayISO()}
                      onClick={() => {
                        setFrom(pr.from());
                        setTo(todayISO());
                      }}
                    >
                      {pr.label === null ? pr.key : t(pr.label)}
                    </button>
                  ))}
                </div>

                <label className="field-chip">
                  <Icon name="calendar" size={14} />
                  <input
                    type="date"
                    value={from}
                    max={to}
                    onChange={(e) => setFrom(e.target.value)}
                    aria-label={t(UI.fromDate)}
                  />
                  <span style={{ color: "var(--ink-3)" }}>→</span>
                  <input
                    type="date"
                    value={to}
                    min={from}
                    onChange={(e) => setTo(e.target.value)}
                    aria-label={t(UI.toDate)}
                  />
                </label>

                <button
                  type="button"
                  className="chip-btn"
                  aria-pressed={era === "all"}
                  title={t(UI.includeLegacy)}
                  onClick={() => setEra(era === "managed" ? "all" : "managed")}
                >
                  {era === "managed" ? t(UI.managedEra) : t(UI.allData)}
                </button>

                {/*
                  Сравнение выключено по умолчанию: включённое, оно удваивает
                  число запросов. Подпись называет, с чем сравнивают, — иначе
                  «+11 %» читается как «к прошлому месяцу», а это предыдущий
                  отрезок той же длины.
                */}
                <button
                  type="button"
                  className="chip-btn"
                  aria-pressed={compare}
                  title={
                    compare
                      ? `${t(UI.comparingAgainst)} ${prevRange.from} → ${prevRange.to}`
                      : t(UI.compareHint)
                  }
                  onClick={() => setCompare((c) => !c)}
                >
                  {compare ? `vs ${prevRange.from} → ${prevRange.to}` : t(UI.compareToPrevious)}
                </button>
              </div>
            )}

            {/*
              Недельный отчёт: свой фильтр вместо периода.

              ОДНА неделя, а не диапазон. Диапазон заново создал бы спор о
              знаменателе, ради которого экран и существует: пайплайн двух
              недель складывать нельзя — лид, пролежавший в работе обе недели,
              посчитался бы дважды.

              Стрелки рядом с селектором, потому что смотрят обычно соседние
              недели: «а на прошлой?» — это одно нажатие, а не открыть список и
              найти в нём дату.
            */}
            {screen === "sales" && (
              <div className="topbar-filters">
                <label className="field-chip">
                  <Icon name="calendar" size={14} />
                  <span className="filter-label">{t(UI.week)}</span>
                  <select
                    value={salesWeek ?? ""}
                    onChange={(e) => setSalesWeek(e.target.value === "" ? null : e.target.value)}
                    aria-label={t(UI.week)}
                    /*
                     * Оформление inline, а не в `app.css`: правило
                     * `.field-chip select` пришлось бы завести в файле стилей,
                     * который в этой волне не мой. Свойства — те же, что у
                     * `.field-chip input`, чтобы селектор не выпадал из пилюли.
                     */
                    style={{
                      background: "none",
                      border: 0,
                      padding: 0,
                      color: "inherit",
                      font: "inherit",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {/* Значение по умолчанию — литерал, а не дата: он должен
                        ехать вместе со временем и не примерзать к неделе. */}
                    <option value="">{t(TX.lastCompleteWeekOption)}</option>
                    {/* Неделя без истории переходов помечена, но выбрать её
                        можно: замолчать её было бы хуже, чем показать с
                        оговоркой. Значением по умолчанию она не станет — это
                        решает сервер. Подпись собирается ОДНОЙ строкой: у
                        `option` содержимое обязано быть текстом. */}
                    {weekMenu.map((w) => (
                      <option key={w.start} value={w.start}>
                        {`${w.start} → ${w.end}${
                          w.pipelineKnown ? "" : ` · ${t(TX.weekPipelineUnknown)}`
                        }`}
                      </option>
                    ))}
                  </select>
                </label>

                <button
                  type="button"
                  className="icon-btn"
                  style={{ width: 26, height: 26 }}
                  title={t(TX.previousWeek)}
                  aria-label={t(TX.previousWeek)}
                  disabled={weekStep(-1) === null}
                  onClick={() => {
                    const next = weekStep(-1);
                    if (next !== null) setSalesWeek(next);
                  }}
                >
                  ←
                </button>
                <button
                  type="button"
                  className="icon-btn"
                  style={{ width: 26, height: 26 }}
                  title={t(TX.nextWeek)}
                  aria-label={t(TX.nextWeek)}
                  disabled={weekStep(1) === null}
                  onClick={() => {
                    const next = weekStep(1);
                    if (next !== null) setSalesWeek(next);
                  }}
                >
                  →
                </button>

                {/*
                  Тот же чип сравнения, но он сравнивает НЕДЕЛИ, а не отрезки
                  той же длины. До 10.09.2026 он на этом экране висел мёртвым:
                  экрана не было в списке экранов со сравнением, и разница на
                  плитках рисовалась всегда, нажат чип или нет. Второй запрос не
                  нужен — предыдущая неделя приезжает в том же массиве.
                */}
                <button
                  type="button"
                  className="chip-btn"
                  aria-pressed={compare}
                  title={compare ? t(TX.comparingAgainstWeek) : t(TX.compareToPreviousWeek)}
                  onClick={() => setCompare((c) => !c)}
                >
                  {compare && salesPrevWeek !== null
                    ? `vs ${salesPrevWeek.start} → ${salesPrevWeek.end}`
                    : t(TX.compareToPreviousWeek)}
                </button>
              </div>
            )}

            <span className="spacer" />

            {/*
              Поиск стоит слева от оформления: он про данные, а оформление про
              то, как на них смотрят.
            */}
            <GlobalSearch nav={searchNav} canScreen={can} />

            {/*
              Всё оформление одной кнопкой. Тема, плотность, шрифт и печать
              относятся не к данным, а к тому, как на них смотрят, и в панели
              фильтров они спорили за место с самими фильтрами.
            */}
            <AppearanceMenu chrome={chrome} isAdmin={me?.isAdmin === true} />

            {me && (
              <span className="user-chip" title={me.isAdmin ? t(UI.administrator) : t(UI.user)}>
                <span className="av">{(me.name || "?").slice(0, 1).toUpperCase()}</span>
                {me.name}
              </span>
            )}
          </div>

          {meta && screen !== "metrics" && (
            <div className="topbar-row second">
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span className="filter-label">{t(UI.parks)}</span>
                <button
                  type="button"
                  className="chip-btn"
                  aria-pressed={parkFilter.length === 0}
                  onClick={() => setParkFilter([])}
                >
                  {t(UI.all)} {meta.parks.length}
                </button>
                {meta.parks.map((pk) => (
                  <button
                    key={pk.park_key}
                    type="button"
                    className="chip-btn"
                    aria-pressed={parkFilter.includes(pk.park_key)}
                    onClick={() =>
                      setParkFilter((cur) =>
                        cur.includes(pk.park_key)
                          ? cur.filter((k) => k !== pk.park_key)
                          : [...cur, pk.park_key],
                      )
                    }
                  >
                    {pk.name}
                  </button>
                ))}
              </div>

              {/*
                Бренды отдельным рядом, а не среди парков. Бренд — не сумма своих
                парков: к нему привязано и то, у чего парка нет, например реклама
                бренда целиком. Смешать их в одну строку значило бы, что одно и то
                же число меняется от способа выбора.
              */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span className="filter-label">{t(UI.brands)}</span>
                <button
                  type="button"
                  className="chip-btn"
                  aria-pressed={brandFilter.length === 0}
                  onClick={() => setBrandFilter([])}
                >
                  {t(UI.all)} {meta.brands.length}
                </button>
                {meta.brands.map((b) => (
                  <button
                    key={b.brand_key}
                    type="button"
                    className="chip-btn"
                    aria-pressed={brandFilter.includes(b.brand_key)}
                    onClick={() =>
                      setBrandFilter((cur) =>
                        cur.includes(b.brand_key)
                          ? cur.filter((k) => k !== b.brand_key)
                          : [...cur, b.brand_key],
                      )
                    }
                  >
                    {b.name}
                  </button>
                ))}
              </div>

              {/*
                Сохранённые наборы. Пунктирная рамка отличает их от фильтров:
                чип вида НЕ показывает состояние, он его ПРИМЕНЯЕТ, и путать эти
                две вещи в одном ряду нельзя.
              */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span className="filter-label">{t(UI.saved)}</span>
                {views.map((v) => (
                  <span key={v.id} className="view-chip">
                    <button type="button" onClick={() => applyView(v)} title={t(TX.applyThisView)}>
                      <i style={{ background: `var(--${v.tint})` }} />
                      {v.name}
                    </button>
                    <button
                      type="button"
                      className="drop"
                      title={t(TX.deleteThisView)}
                      aria-label={`${t(UI.deleteView)}: ${v.name}`}
                      onClick={() => void removeView(v)}
                    >
                      <Icon name="close" size={11} width={3} />
                    </button>
                  </span>
                ))}

                {naming === null ? (
                  <button
                    type="button"
                    className="icon-btn"
                    style={{ width: 26, height: 26 }}
                    title={t(TX.saveAsView)}
                    aria-label={t(UI.saveCurrentFilters)}
                    onClick={() => setNaming("")}
                  >
                    +
                  </button>
                ) : (
                  <form
                    style={{ display: "flex", gap: 6, alignItems: "center" }}
                    onSubmit={(e) => {
                      e.preventDefault();
                      void saveCurrentView(naming);
                    }}
                  >
                    <input
                      autoFocus
                      value={naming}
                      maxLength={60}
                      placeholder={t(TX.nameThisView)}
                      onChange={(e) => setNaming(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Escape") setNaming(null);
                      }}
                      style={{ width: 150, padding: "3px 9px", fontSize: 12.5 }}
                      aria-label={t(UI.viewName)}
                    />
                    <button type="submit" className="btn primary" style={{ padding: "3px 11px" }}>
                      {t(UI.save)}
                    </button>
                  </form>
                )}
              </div>
            </div>
          )}
        </header>

      <main className="main">
        <ScreenHead
          screen={screen}
          /*
           * У дайджеста дата отчётного дня стоит В ЗАГОЛОВКЕ, тем же кеглем.
           *
           * Она и раньше была на экране, но мелкой блёклой подписью под ним, и
           * заказчик на сдаче прямо на это указал: «там просто 10 сентября так
           * мелко написано? Хер поймёшь». Экран, который показывает ОДНИ сутки,
           * обязан называть их так же громко, как себя.
           *
           * Полночь в дате обязательна: голое «2026-09-12» разбирается как UTC
           * и западнее Гринвича съезжает на сутки назад.
           */
          title={
            screen === "digest" && digestData
              ? `${t(SCREEN.digest.title)} (${weekdayDate(`${digestData.day}T00:00:00`)})`
              : t(SCREEN[screen].title)
          }
          sub={
            screen === "metrics" ? (
              t(SCREEN.metrics.sub)
            ) : screen === "digest" ? (
              /* У дайджеста свой подзаголовок: печатать над ним выбранный
                 период было бы ложью — период к нему не применяется, и сам
                 экран называет свои сутки строкой ниже. */
              t(SCREEN.digest.sub)
            ) : screen === "sales" ? (
              /*
                Здесь стояла строка периода из верхнего фильтра, и заказчик на
                неё указал: «рядом с заголовком всё ещё динамически обновляется
                строка с периодом — и она запутывает юзера». Теперь строка
                называет ВЫБРАННУЮ НЕДЕЛЮ и говорит, полная она или нет.
                Порядок правки был обратный удобному: сначала с экрана снят
                период, и только потом подпись, — иначе правка спрятала бы
                ошибку вместо того, чтобы её исправить.
              */
              <>
                {salesMeta === null ? (
                  t(UI.loading)
                ) : (
                  <>
                    {salesMeta.start} → {salesMeta.end}
                    {/*
                      Полная неделя или нет — сказано ВСЕГДА, а не только когда
                      что-то не так: это первое, что нужно знать про недельные
                      числа, и молчание пришлось бы толковать.
                    */}
                    {` · ${
                      salesMeta.is_current
                        ? t(TX.weekInProgressMark)
                        : salesMeta.is_last_complete
                          ? t(TX.lastCompleteWeekMark)
                          : t(TX.completeWeekMark)
                    }`}
                    {/* Пометка про неполноту истории стоит РЯДОМ с неделей, а
                        не в сноске внизу: по этой строке читают, чему верить. */}
                    {salesMeta.pipeline_known ? "" : ` · ${t(TX.weekPipelineUnknown)}`}
                  </>
                )}
                {sales ? ` · ${sales.queryMs} ms` : ""}
                {screenBusy ? ` · ${t(UI.loading)}` : ""}
              </>
            ) : (
              <>
                {/*
                  На Customer journey переключатель эпохи не действует, и печатать
                  над числами «managed era» было бы ложью: цикл может начаться в
                  legacy и закончиться в managed, поэтому фильтра эпохи у циклов
                  нет вовсе. Честнее назвать это.
                */}
                {from} → {to} ·{" "}
                {screen === "journey"
                  ? t(TX.bothErasCycles)
                  : screen === "b2b"
                    ? t(TX.b2bNoEra)
                    : era === "managed"
                    ? t(ERA.managed)
                    : t(TX.allDataInclLegacy)}
                {overview ? ` · ${overview.queryMs} ms` : ""}
                {screenBusy ? ` · ${t(UI.loading)}` : ""}
              </>
            )
          }
        />

        {/*
          * Обратный случай: legacy ВКЛЮЧЁН, и надо честно сказать, чему в нём
          * верить. Заказчик решил так прямо: «до 1 июля мы можем показывать
          * данные, однако просто клиента предупредим что они до этого периода
          * зачастую будут неверны». Прятать их было бы хуже — управляющий видел
          * бы пустой год вместо оговорки.
          *
          * Формулировка опирается на замер, а не на общие слова: ВСЕ переходы
          * стадий в базе датированы 01.07.2026 или позже, ни одного раньше.
          * Значит у лида, созданного в 2025-м, воронка показывает стадию, в
          * которой он стоял к началу сбора, а не путь, которым шёл.
          *
          * Числа сюда не вписаны намеренно: захардкоженная цифра под воронкой
          * уже однажды разошлась с данными, и это был мой же дефект.
          */}
        {/* На недельных продажах эпохи и периода нет вовсе: эндпоинт их не
            читает, а органы управления с экрана сняты. Оговорка про границу
            эпох там говорила бы о фильтре, которого на экране не существует. */}
        {screen !== "metrics" &&
          screen !== "journey" &&
          screen !== "sales" &&
          overview?.leads.historyFrom &&
          era === "all" &&
          from < overview.leads.historyFrom && (
            <div className="note" style={{ marginBottom: 14 }}>
              <b>
                {t(TX.legacyWarnTitle)} {overview.leads.historyFrom}.
              </b>{" "}
              {t(TX.legacyWarnBody)}
            </div>
          )}

        {/*
          * Баннер обещает, что legacy исключён. На Customer journey это неверно:
          * там фильтра эпохи нет, и числа включают обе. Поэтому экран исключён
          * из условия — обещание должно совпадать с тем, что считает API.
          */}
        {screen !== "metrics" &&
          screen !== "journey" &&
          screen !== "sales" &&
          overview?.leads.historyFrom &&
          era === "managed" &&
          from < overview.leads.historyFrom && (
          <div className="note" style={{ marginBottom: 14 }}>
            {t(TX.managedNote)}{" "}
            <button
              className="chip-btn"
              style={{ marginLeft: 6 }}
              onClick={() => setEra("all")}
            >
              {t(TX.includeOlder)}
            </button>
          </div>
        )}

        {error && <div className="card err">{error}</div>}
        {loading && !overview && <div className="loading">{t(UI.loading)}</div>}

        {/*
          Объяснение вместо значков, когда сравнивать не с чем. Молча не
          показать сравнение тоже нельзя: человек нажал кнопку и вправе знать,
          почему ничего не изменилось.
        */}
        {/* На недельных продажах сравнение идёт с предыдущей НЕДЕЛЕЙ из того же
            массива, а не с отрезком той же длины, поэтому «сравнивать не с
            чем» там неверно по построению. */}
        {compareBlocked && screen !== "sales" && (
          <div className="note" style={{ marginBottom: 14 }}>
            <b>{t(TX.compareBlockedTitle)}</b> {t(TX.compareBlockedBody1)} {prevRange.to}{" "}
            {t(TX.compareBlockedBody2)} {historyFrom}. {t(TX.compareBlockedBody3)}
          </div>
        )}

        {screen === "digest" && digestData && <DigestScreen report={digestData} />}
        {screen === "digest" && !digestData && <div className="loading">{t(UI.loading)}</div>}

        {overview && screen === "overview" && (
          <>
            <div className="grid cols-5" style={{ marginBottom: 14 }}>
              <Kpi
                label={t(UI.conversations)}
                value={num(leadKpi.leads ?? 0)}
                delta={delta("leads")}
                drill={{
                  title: t(UI.conversations),
                  hint: `${t(TX.drillAllLeads)} · ${t(TX.drillByLeadCreated)}`,
                  slice: {},
                }}
                foot={
                  <>
                    {/*
                      Подпись «закрыто как мусор» СОЗНАТЕЛЬНО не кликабельна.
                      Мусор — это две стадии, JUNK (1 262) и Historical lead
                      (145), и вместе они дают ровно то число, что на подписи, —
                      1 407 (замер 10.09.2026, период 01.07-10.09, managed).
                      Одним `stageId` две стадии не выбрать, а `isDump` в
                      публичном `LeadSlice` фронтенда нет: на сервере срез есть и
                      сверен до единицы, в `web/src/lib/api.ts` он не объявлен, и
                      этот файл не мой. Список по одной из двух стадий показал бы
                      1 262 строки под числом 1 407.
                    */}
                    {num(leadKpi.dumped ?? 0)} {t(TX.closedAsJunk)}
                    {prevLeadKpi !== null &&
                      ` · ${num(prevLeadKpi.leads ?? 0)} ${t(TX.inPreviousPeriod)}`}
                  </>
                }
              />
              {/*
                ОДНА ПЛИТКА, А НЕ ДВЕ. Рядом стояла «Дошли до менеджера ОП» —
                1 797 и 16,3 %, — и заказчик справедливо спросил, зачем на
                экране две одинаковые метрики: подписи под ними читались почти
                дословно одинаково.

                Одинаковыми они и были. Квалифицированные ПОГЛОЩАЮТ дошедших:
                `reached_handoff` против `reached_handoff OR has_won_deal`. Вся
                разница — 113 лидов на всей managed-эпохе, ушедших в выигранную
                сделку минуя обе стадии. Два числа, отличающиеся на процент, с
                почти совпадающими подписями — это не выбор, а вопрос «какому
                верить», и он возникал бы заново каждый раз.

                Осталось определение самого заказчика от 08.09.2026: «побывал на
                Interested или Park Tour, ну и успешные тоже понятное дело».
                С 29.09.2026 это имя и это выражение — везде, где считается
                когорта лидов (источники, парки, бренды, маркетинг, дайджест,
                выгрузки, пути клиента). Прежнее «Reached a salesperson» осталось
                только на экране конверсии — как вход на стадии в периоде, и
                называется там своим именем: «Entered Interested / Park Tour».

                Признак `qualified` в срезе — тот самый «нужен один признак»,
                который тут стоял примечанием: пары `reached` + `won` не хватало,
                сервер соединяет их через AND и давал 74 вместо 1 250.
              */}
              <Kpi
                label={t(TX.qualifiedLeads)}
                value={num(leadKpi.qualified_leads ?? 0)}
                delta={delta("qualified_leads")}
                drill={{
                  title: t(TX.qualifiedLeads),
                  hint: t(TX.drillByLeadCreated),
                  slice: { qualified: true },
                }}
                foot={
                  <>
                    {pct(leadKpi.qualified_leads ?? 0, leadKpi.leads ?? 0)}{" "}
                    {t(TX.footQualified)}
                    {(leadKpi.qualified_leads ?? 0) > (leadKpi.reached_handoff ?? 0) && (
                      <>
                        {" · "}
                        {/* Второе слагаемое названо и раскрывается: без него
                            «1 910» выглядит просто как другое число для того же
                            самого, и вопрос заказчика вернётся. */}
                        <DrillNum
                          drill={{
                            title: t(TX.drillStraightToDeal),
                            hint: t(TX.drillByLeadCreated),
                            slice: { won: true, reached: false },
                          }}
                        >
                          {num((leadKpi.qualified_leads ?? 0) - (leadKpi.reached_handoff ?? 0))}
                        </DrillNum>{" "}
                        {t(TX.footStraightToDeal)}
                      </>
                    )}
                  </>
                }
              />
              <Kpi
                label={t(TX.becameDeal)}
                value={num(leadKpi.with_deal ?? 0)}
                delta={delta("with_deal")}
                drill={{
                  title: t(TX.becameDeal),
                  hint: t(TX.drillByLeadCreated),
                  slice: { hasDeal: true },
                }}
                foot={
                  <>
                    {`${pct(leadKpi.with_deal ?? 0, leadKpi.leads ?? 0)} ${t(TX.footDeal)} `}
                    {/* Второе число подписи — другая совокупность, чем плитка
                        (346 против 161), поэтому у него своя кнопка и свой
                        заголовок, а не общий с плиткой. */}
                    <button
                      type="button"
                      style={DRILL_VALUE}
                      title={t(TX.drillOpenList)}
                      onClick={() =>
                        setDrill({
                          title: t(TX.drillWonLeads),
                          hint: t(TX.drillByLeadCreated),
                          slice: { won: true },
                        })
                      }
                    >
                      {num(leadKpi.with_won_deal ?? 0)}
                    </button>
                    {` ${plural(leadKpi.with_won_deal ?? 0, TX.completedEvents)}`}
                  </>
                }
              />
              {/*
                ВЫРУЧКА С БАНКЕТОВ — ЧЕКИ ПРАЗДНИКОВ, и под ней открываются они
                сами (миграция 090, 04.10.2026).

                До этого плитка стояла на строках банкетных категорий кассы и не
                обвязывалась вовсе: в той выгрузке нет номера чека. Сверка
                сентября считала праздник ЧЕКОМ целиком — банкетный заказ или
                заказ с пакетом, со всем, что на празднике купили, — и плитка
                перешла на то же определение: 1 285 008,76 AED за сентябрь
                против 1 116 440,29 AED по категориям.

                Второе число подписи — те же праздники в сделках CRM, с
                исключениями сверки. Это другое население (сделки, а не чеки),
                поэтому у него свой список, а число исключений открывает их
                перечень: почему CRM посчитана не так, как стоит в сделках.
              */}
              <Kpi
                label={t(TX.banquetRevenueSyrve)}
                value={money(totals.banquet, "AED", { compact: true, withCode: false })}
                unit="AED"
                drill={{
                  kind: "checks",
                  title: t(TX.banquetRevenueSyrve),
                  hint: t(TX.hintPartyChecks),
                  slice: {},
                }}
                foot={
                  <>
                    {num(totals.partyChecks)} {plural(totals.partyChecks, TX.partyChecksCounted)} ·{" "}
                    {t(TX.footBanquetSameParties)}{" "}
                    <DrillNum
                      drill={{
                        kind: "deals",
                        title: t(TX.drillCrmParties),
                        hint: t(TX.hintCrmParties),
                        slice: { dateBasis: "party", outcome: "held" },
                      }}
                    >
                      {money(dealKpi.revenue_won_aed_minor, "AED", { compact: true })}
                    </DrillNum>
                    {Number(dealKpi.exceptions_affecting ?? 0) > 0 && (
                      <>
                        {" ("}
                        <DrillNum
                          drill={{
                            kind: "exceptions",
                            title: t(TX.drillExceptions),
                            hint: t(TX.hintExceptions),
                            status: "affecting",
                          }}
                        >
                          {num(Number(dealKpi.exceptions_affecting))}
                        </DrillNum>{" "}
                        {plural(Number(dealKpi.exceptions_affecting), TX.exceptionsApplied)}
                        {")"}
                      </>
                    )}{" "}
                    · {t(TX.footBanquetDoNotAdd)}
                  </>
                }
              />
            </div>

            <div className="grid cols-2" style={{ marginBottom: 14 }}>
              {/*
                ВОРОНКА И ГРАФИК ПО ДНЯМ НЕ ОБВЯЗАНЫ ОТСЮДА — не по недостатку
                данных, а потому что открывать нечем.

                Срезы проверены и совпадают до единицы (замер 10.09.2026,
                01.07-10.09, managed): семь ступеней воронки — `reachedStageId`
                (812 / 780 / 1 001 / 1 579 / 1 035 / 241 / 363), приписка
                «сейчас вне воронки» — `stageId` (все 18 стадий, включая
                FOLLOW UP 812 против 1 579 по факту прохождения). То есть срезы
                верные, но `LeadFunnel` и `DailyChart` живут в widgets.tsx и не
                принимают ни открывашки, ни `useDrillOpener()`, а этот файл не
                мой. `reachedStageId` вдобавок не объявлен в `LeadSlice`
                фронтенда.

                У графика по дням есть ВТОРАЯ, независимая причина. Проверялось,
                нельзя ли получить точку, сузив период до одного дня: нельзя.
                Точка сгруппирована по дате Дубая, а период списка сравнивается
                с `date_create` в часовом поясе базы, и это расходится на
                четыре часа. Замер: 08.09 плитка 120 — сужение даёт 133; 09.09
                плитка 18 — сужение даёт 124; в ряду вообще есть 30.06.2026 (49
                лидов) при периоде с 01.07, а сужение на этот день даёт ноль.
                Обвязка «примерно тем же» здесь была бы худшим из вариантов.
              */}
              <Card title={t(TX.leadFunnel)} hint={t(TX.leadFunnelHint)}>
                <LeadFunnel
                  funnel={overview.leads.funnel}
                  stageNow={overview.leads.stageNow}
                  totalLeads={leadKpi.leads ?? 0}
                  historyFrom={overview.leads.historyFrom}
                  era={era}
                />
              </Card>
              <Card title={t(TX.leadsPerDay)}>
                <DailyChart daily={overview.leads.daily} />
              </Card>
            </div>

            <div className="grid cols-2" style={{ marginBottom: 14 }}>
              <Card title={t(UI.sources)} hint={t(TX.sourcesHint)}>
                <ChannelTable rows={overview.leads.byChannel} onDrill={setDrill} />
              </Card>
              {/*
                ТАБЛИЦА КОНВЕРСИИ НЕ ОБВЯЗАНА: строку нечем адресовать.

                Когорта у этой таблицы своя — не «лид создан в периоде», а «лид
                вошёл в продажи внутри периода», и она проверена: срез
                `cohort: 'entered_handoff'` даёт 1 292 / 186 / 87, ровно суммы
                колонок «Дошли», «Сделка», «Завершено» (замер 10.09.2026).

                Но `pipelineConversion` возвращает только `park_name` —
                `COALESCE(dim_park.name, '(not identified)')`, — и `park_key` в
                ответе НЕТ. Срез списка адресует парк ключом. Сводить имя обратно
                в ключ через справочник парков — это отбор по подписи: имя
                меняется в CRM без нашего участия, и в день переименования список
                молча опустеет под непустым числом. И `cohort` в `LeadSlice`
                фронтенда тоже не объявлен. См. отчёт: нужен `park_key` в ответе.
              */}
              <Card title={t(SCREEN.pipeline.title)} hint={t(TX.pipelineHint)}>
                <div className="scroll-x">
                  <table className="data">
                    <thead>
                      <tr>
                        <Th sort={Sconv} col="park">{t(UI.park)}</Th>
                        <Th sort={Sconv} col="reached" num>{t(TX.colReached)}</Th>
                        <Th sort={Sconv} col="deal" num>{t(TX.colDeal)}</Th>
                        <Th sort={Sconv} col="completed" num>{t(TX.colCompleted)}</Th>
                        <Th sort={Sconv} col="conv" num>{t(TX.colConv)}</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {Sconv.rows.map((r) => (
                        <tr key={r.park_name}>
                          <td>{dbLabel(SENTINEL, r.park_name, r.park_name)}</td>
                          <td className="num">{num(r.reached_handoff)}</td>
                          <td className="num">{num(r.with_deal)}</td>
                          <td className="num">{num(r.won_deal)}</td>
                          <td className="num">{r.conv_to_deal_pct === null ? "—" : pctValue(r.conv_to_deal_pct)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>

            <Card title={t(TX.campaignTagging)} hint={t(TX.campaignTaggingHint)}>
              <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
                {overview.leads.utmCoverage.map((u) => (
                  <div key={u.basis_key}>
                    <div style={{ fontSize: 20, fontWeight: 700 }}>
                      {/*
                        Кликабельны ТРИ КОРЗИНЫ ИЗ ПЯТИ, и это следствие замера,
                        а не осторожности.

                        Плитки считаются по `basis_key` — пяти значениям,
                        которые сервер выводит здесь же (`dashboard.ts`), а срез
                        списка отбирает по `basis` — четырём значениям
                        `core.v_lead_source`. Совпадают они только на трёх:
                        замер 10.09.2026 (01.07-10.09, managed) даёт
                        client_tag 153 = 153, analytics_tag 524 = 524,
                        chat_link 5 = 5.

                        Две остальные корзины в `basis` НЕОТЛИЧИМЫ: «метки нет»
                        (7 877) и «метка есть, но площадку не называет» (740)
                        обе сидят в `basis = 'source_field'`, и он даёт 8 617 —
                        ровно их сумму. Список под любой из двух показал бы
                        8 617 строк под числом 740 или 7 877. Нужен срез по
                        ключу корзины — см. отчёт.
                      */}
                      {DRILLABLE_BASIS.has(u.basis_key) ? (
                        <button
                          type="button"
                          style={DRILL_VALUE}
                          title={t(TX.drillOpenList)}
                          onClick={() =>
                            setDrill({
                              title: dbLabel(UTM_BASIS, u.basis_key, u.field_set),
                              hint: `${t(TX.campaignTagging)} · ${t(TX.drillByLeadCreated)}`,
                              slice: { basis: u.basis_key },
                            })
                          }
                        >
                          {num(u.leads)}
                        </button>
                      ) : (
                        num(u.leads)
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--ink-3)" }}>
                      {dbLabel(UTM_BASIS, u.basis_key, u.field_set)}
                    </div>
                  </div>
                ))}
              </div>
              <div className="note" style={{ marginTop: 12 }}>
                {t(TX.campaignTaggingNote)}
              </div>
            </Card>
          </>
        )}

        {overview && screen === "parks" && (
          <>
            {/*
              НА ЭТОМ ЭКРАНЕ ДЕТАЛИЗАЦИИ НЕТ НИ ПОД ОДНОЙ ЦИФРОЙ, и это проверено
              по коду, а не принято на слово.

              Все четыре плитки и вся таблица по паркам считаются из
              `mart.park_revenue_daily` (`sumParks` выше складывает её же
              строки). Март собран из отчёта Syrve с группировкой по дате,
              отделу, категории, блюду и типу оплаты — идентификатора чека там
              нет вовсе, а `receipts` приходит агрегатом на строку отчёта.
              Записи, в которую можно перейти, под этими цифрами не существует.

              «Билетов продано» — та же выгрузка, поле `items`, то есть сумма
              строк отчёта, а не список билетов. «Онлайн-билеты» приходят с
              сайта бронирования тем же дневным агрегатом.

              Карта детализации не требует по другой причине: она уже фильтр —
              выбор парка меняет период всего экрана, и это сильнее списка.
            */}
            <div className="grid cols-4" style={{ marginBottom: 14 }}>
              <Kpi
                label={t(TX.posRevenue)}
                value={money(totals.revenue, "AED", { compact: true, withCode: false })}
                unit="AED"
                delta={parkDelta("revenue", "money")}
              />
              <Kpi
                label={t(UI.tickets)}
                value={money(totals.tickets, "AED", { compact: true, withCode: false })}
                unit="AED"
                foot={pct(totals.tickets, totals.revenue)}
                delta={parkDelta("tickets", "money")}
              />
              <Kpi
                label={t(TX.onlineTickets)}
                value={money(totals.online, "AED", { compact: true, withCode: false })}
                unit="AED"
                foot={t(TX.fromBookingSite)}
                delta={parkDelta("online", "money")}
              />
              {/*
                Проданные билеты вместо «гостей»: поле guests приходило из OLAP
                на каждой строке отчёта, и сумма по группам выручки считала один
                заказ несколько раз — 3 964 против 641 билета за один день по
                одному парку.
              */}
              <Kpi
                label={t(TX.ticketsSold)}
                value={num(totals.ticketsSold)}
                foot={t(TX.footTicketsSold)}
                delta={parkDelta("ticketsSold", "count")}
              />
            </div>

            <div className="grid cols-2" style={{ marginBottom: 14 }}>
              <Card
                title={t(TX.whereParksAre)}
                hint={
                  parksWithoutData > 0 ? `${parksWithoutData} ${t(TX.withoutSyrve)}` : undefined
                }
              >
                <ParkMap parks={geo} revenue={revenue} onSelect={(k) => setParkFilter(k ? [k] : [])} />
              </Card>
              <Card title={t(TX.revenueMix)} hint={t(TX.posCategories)}>
                {/* Кольцо — состав кассы по категориям: доли складываются в итог,
                    поэтому банкеты здесь — банкетные КАТЕГОРИИ, а не чеки
                    праздников из колонки ниже (чек праздника включает и еду). */}
                <RevenueSplit
                  tickets={totals.tickets}
                  food={totals.food}
                  banquet={totals.banquetCategory}
                  other={totals.other}
                />
              </Card>
            </div>

            <Card title={t(TX.revenueByPark)}>
              <div className="scroll-x">
                <table className="data">
                  <thead>
                    <tr>
                      <Th sort={Srev} col="park">{t(UI.park)}</Th>
                      <Th sort={Srev} col="revenue" num>{t(TX.colPosRevenue)}</Th>
                      <Th sort={Srev} col="tickets" num>{t(UI.tickets)}</Th>
                      <Th sort={Srev} col="food" num>{t(TX.colFood)}</Th>
                      <Th sort={Srev} col="banquet" num>{t(TX.colBanquet)}</Th>
                      <Th sort={Srev} col="ticketsSold" num>{t(TX.ticketsSold)}</Th>
                      <Th sort={Srev} col="online" num>{t(TX.colOnline)}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {Srev.rows.map((p) => {
                      const empty = Number(p.revenue_aed_minor) === 0;
                      return (
                        <tr key={p.park_key} style={empty ? { color: "var(--ink-3)" } : undefined}>
                          <td>{meta?.parks.find((m) => m.park_key === p.park_key)?.name ?? p.park_key}</td>
                          <td className="num">
                            {empty ? t(TX.noAccess) : money(p.revenue_aed_minor, "AED", { withCode: false })}
                          </td>
                          <td className="num">{empty ? "—" : money(p.tickets_aed_minor, "AED", { withCode: false })}</td>
                          <td className="num">{empty ? "—" : money(p.food_aed_minor, "AED", { withCode: false })}</td>
                          <td className="num">{empty ? "—" : money(p.banquet_aed_minor, "AED", { withCode: false })}</td>
                          <td className="num">{empty ? "—" : num(Math.round(Number(p.tickets_sold)))}</td>
                          <td className="num">{money(p.online_revenue_aed_minor, "AED", { withCode: false })}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="note" style={{ marginTop: 12 }}>{t(TX.parkRevenueNote)}</div>
            </Card>
          </>
        )}
        {screen === "brands" && brands && (
          <BrandsScreen report={brands} onPickPark={(k) => setParkFilter([k])} />
        )}
        {screen === "pipeline" && pipeline && (
          <PipelineScreen report={pipeline} delta={screenDelta} />
        )}
        {screen === "operations" && ops && <OperationsScreen report={ops} delta={screenDelta} />}
        {screen === "marketing" && mkt && (
          <MarketingScreen report={mkt} era={era} onDrill={setDrill} delta={screenDelta} />
        )}
        {screen === "sales" && sales && <SalesScreen report={sales} compare={compare} />}
        {screen === "journey" && journey && (
          <JourneyScreen report={journey} canLookup={can("journey_lookup")} />
        )}
        {/*
          * Экран сам грузит свои данные: у него нет ни периода из шапки, ни
          * когорты по умолчанию — окно выбирается руками, а создание требует
          * отдельного нажатия.
          */}
        {screen === "client_base" && <ClientBaseScreen />}
        {screen === "b2b" && <B2bScreen filters={filters} />}
        {screen === "export" && <ExportScreen filters={filters} />}
        {screen === "admin" && me?.isAdmin && <AdminScreen />}
        {screen === "metrics" && (
          <Suspense fallback={<div className="loading">…</div>}>
            <ReferenceScreen />
          </Suspense>
        )}

        {screenBusy && !brands && !pipeline && !ops && !mkt && !sales && (
          <div className="loading">{t(UI.loading)}</div>
        )}
      </main>
      </div>

      {/* Панель со списком лидов живёт на уровне приложения: она общая для всех
          экранов и должна перекрывать разметку, а не встраиваться в карточку. */}
      {/*
        Панель одна на приложение и общая для всех экранов: она должна
        перекрывать разметку, а не встраиваться в карточку. Ветка выбирается по
        виду списка — под плиткой «выиграна, но депозит не записан» нужен
        список СДЕЛОК: замерено, что 11 из 13 таких сделок вообще без лида, и
        список лидов показал бы две строки под числом тринадцать.
      */}
      {/*
        Панель лидов берёт ТОЛЬКО свою ветку — по признаку, а не перечнем
        чужих. Перечень «не сделки, не диалоги, не чеки, не исключения»
        пропустил бы сюда каждую новую ветку: так брони сайта билетов открылись
        списком лидов с нулём строк, и типы этого не поймали — срез броней
        структурно совместим со срезом лидов.
      */}
      <LeadDrawer
        drill={drill !== null && (drill.kind === undefined || drill.kind === "leads") ? drill : null}
        /* Плитка вправе задать своё окно — тем же приёмом, что и у сделок.
           Дайджест период сверху не применяет, и его списки обязаны отбирать
           ровно ту совокупность, которую посчитала карточка. */
        filters={
          drill !== null &&
          (drill.kind === undefined || drill.kind === "leads") &&
          drill.filters !== undefined
            ? { ...filters, ...drill.filters }
            : filters
        }
        onClose={() => setDrill(null)}
      />
      <DealDrawer
        drill={drill !== null && drill.kind === "deals" ? drill : null}
        /* Плитка вправе задать своё окно: дайджест считает сутки, а не
           период с верхней панели, и список под его цифрой обязан отбирать
           те же сутки. Накладывается поверх, а не вместо: парки и бренды
           остаются выбранными. */
        filters={
          drill !== null && drill.kind === "deals" && drill.filters !== undefined
            ? { ...filters, ...drill.filters }
            : filters
        }
        onClose={() => setDrill(null)}
      />
      {/*
        Третья панель — диалоги открытой линии. Своё окно ей нужно так же, как
        двум другим: карточка «Никто не ответил» считает свои семь дней, а не
        период с верхней панели.
      */}
      <ConversationDrawer
        drill={drill !== null && drill.kind === "conversations" ? drill : null}
        filters={
          drill !== null && drill.kind === "conversations" && drill.filters !== undefined
            ? { ...filters, ...drill.filters }
            : filters
        }
        onClose={() => setDrill(null)}
      />
      {/* Чеки праздников под банкетной кассой и исключения сверки под
          праздниками CRM (миграция 090). */}
      <PartyCheckDrawer
        drill={drill !== null && drill.kind === "checks" ? drill : null}
        filters={
          drill !== null && drill.kind === "checks" && drill.filters !== undefined
            ? { ...filters, ...drill.filters }
            : filters
        }
        onClose={() => setDrill(null)}
      />
      <ExceptionDrawer
        drill={drill !== null && drill.kind === "exceptions" ? drill : null}
        filters={
          drill !== null && drill.kind === "exceptions" && drill.filters !== undefined
            ? { ...filters, ...drill.filters }
            : filters
        }
        onClose={() => setDrill(null)}
      />
      {/* Брони сайта билетов под блоком «Онлайн-билеты по источникам» на
          экране Marketing (миграция 093). */}
      <TicketBookingDrawer
        drill={drill !== null && drill.kind === "tickets" ? drill : null}
        filters={
          drill !== null && drill.kind === "tickets" && drill.filters !== undefined
            ? { ...filters, ...drill.filters }
            : filters
        }
        onClose={() => setDrill(null)}
      />
      {/* Оплаты из блока «Deposits to fix» на экране Pipeline conversion:
          на лиде или без сделки (миграция 097). */}
      <PaymentDrawer
        drill={drill !== null && drill.kind === "payments" ? drill : null}
        filters={
          drill !== null && drill.kind === "payments" && drill.filters !== undefined
            ? { ...filters, ...drill.filters }
            : filters
        }
        onClose={() => setDrill(null)}
      />
      {/* Сделки воронки B2B под любым числом экрана B2B (миграция 148). */}
      <B2bDealDrawer
        drill={drill !== null && drill.kind === "b2b" ? drill : null}
        filters={filters}
        onClose={() => setDrill(null)}
      />
    </div>
    </DrillProvider>
  );
}

function Login({ onDone }: { onDone: () => void }): React.ReactElement {
  const [username, setUsername] = useState("admin");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const { token } = await api.login(username, password);
      setToken(token);
      onDone();
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : t(TX.signInFailed));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login">
      {/*
        Левая половина — сцена: лучи, персонаж холдинга и реплика в облачке.
        Лучи собраны градиентом, а не картинкой: PNG из брендбука несёт в себе
        впечатанный вордмарк и годится только там, где он и нужен.
      */}
      <div className="login-stage" aria-hidden="true">
        <div className="login-rays" />
        <img
          className="login-hero"
          src="/brand/characters/zantaro-easycrm.png"
          alt=""
        />
        <div className="login-bubble">{t(TX.tagline)}</div>
      </div>

      <div className="login-form-wrap">
        <form className="login-form" onSubmit={submit}>
          <img
            className="login-logo"
            src="/brand/logo/zamania-wordmark-white.png"
            alt="Zamania"
          />
          <div className="frieze" style={{ margin: 0, width: 200 }} aria-hidden="true" />
          <div>
            <div className="login-title">{t(UI.analytics)}</div>
            <div className="login-sub">{t(TX.loginSub)}</div>
          </div>
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder={t(TX.login)}
            aria-label={t(TX.login)}
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t(TX.password)}
            aria-label={t(TX.password)}
          />
          {err !== null && <div className="error">{err}</div>}
          <button className="btn primary" type="submit" disabled={busy} style={{ justifyContent: "center", padding: "10px 16px" }}>
            {busy ? t(TX.signingIn) : t(TX.signIn)}
          </button>
          {bootToken() === null && (
            <div className="login-note">{t(TX.loginNote)}</div>
          )}
        </form>
      </div>
    </div>
  );
}
